import {
  Add,
  Category,
  Delete,
  Domain,
  Edit,
  HomeWork,
  OpenInNew,
  Palette,
  Save,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ConfirmationDialog from "../components/dialogs/ConfirmationDialog";
import { FeedbackSnackbar } from "../components/design/FeedbackSnackbar";
import {
  dashboardCardSx,
  dashboardPagePaddingSx,
  dashboardPageSx,
  dashboardSectionTitleSx,
} from "../components/layout/dashboardStyles";
import { staysApi } from "../data/api/StaysApi";
import { openExperiencePreview } from "../utils/experiencePreview";
import {
  StayProperty,
  StaysCategory,
  StaysConfig,
  StaysReservationRequest,
  StaysReservationStatus,
} from "../data/models/Experience/ExperienceModels";
import { StoreDomain, StorePageConfig, StorePageId } from "../data/models/Experience/ExperienceSharedModels";
import {
  domainStatusColor,
  experiencePageLabels,
  experienceToggleGroupSx,
  mergeExperiencePages,
  SnackbarSeverity,
} from "./experienceAdmin/shared";

type StaysTab = "settings" | "design" | "pages" | "seo" | "properties" | "domains" | "reservations";

const basePages: StorePageConfig[] = [
  { id: "home", slug: "", title: "Inicio", navLabel: "Inicio", footerLabel: "Inicio", contentTitle: "Inicio", contentBody: "", showInNavbar: true, showInFooter: false, isEnabled: true },
  { id: "about", slug: "nosotros", title: "Nosotros", navLabel: "Nosotros", footerLabel: "Nosotros", contentTitle: "Nosotros", contentBody: "Cuenta la experiencia de hospedaje, ubicaciones y propuesta de valor.", showInNavbar: true, showInFooter: true, isEnabled: true },
  { id: "contact", slug: "contactanos", title: "Contáctanos", navLabel: "Contáctanos", footerLabel: "Contáctanos", contentTitle: "Contáctanos", contentBody: "Comparte tus canales de atención, horarios y ubicaciones.", showInNavbar: true, showInFooter: true, isEnabled: true },
  { id: "terms", slug: "terminos-y-condiciones", title: "Términos y condiciones", navLabel: "Términos", footerLabel: "Términos y condiciones", contentTitle: "Términos y condiciones", contentBody: "Agrega aquí políticas de reserva, cancelación y uso del alojamiento.", showInNavbar: false, showInFooter: true, isEnabled: true },
  { id: "privacy", slug: "politicas-de-privacidad", title: "Políticas de privacidad", navLabel: "Privacidad", footerLabel: "Políticas de privacidad", contentTitle: "Políticas de privacidad", contentBody: "Agrega aquí como recolectas, usas y proteges los datos de tus huéspedes.", showInNavbar: false, showInFooter: true, isEnabled: true },
];

const defaultConfig: Partial<StaysConfig> = {
  brandName: "",
  slug: "",
  templateId: "stays-default",
  experienceType: "stays",
  status: "draft",
  pages: basePages,
};

const defaultCategory: Partial<StaysCategory> = {
  name: "",
  description: "",
  isActive: true,
  sortOrder: 0,
};

const defaultProperty: Partial<StayProperty> = {
  name: "",
  description: "",
  categoryId: "",
  pricePerNight: 0,
  guestCapacity: 2,
  bedroomCount: 1,
  bathroomCount: 1,
  location: "",
  imageUrl: "",
  amenities: [],
  availabilityNotes: "",
  isActive: true,
  sortOrder: 0,
};

const tabPaths: Record<StaysTab, string> = {
  settings: "/dashboard/stays/settings",
  design: "/dashboard/stays/design",
  pages: "/dashboard/stays/pages",
  seo: "/dashboard/stays/seo",
  properties: "/dashboard/stays/properties",
  domains: "/dashboard/stays/domains",
  reservations: "/dashboard/stays/reservations",
};

const reservationStatusLabels: Record<StaysReservationStatus, string> = {
  pending: "Pendiente",
  contacted: "Contactado",
  confirmed: "Confirmado",
  cancelled: "Cancelado",
};

const reservationStatusColor = (status: StaysReservationStatus) => {
  if (status === "confirmed") return "success" as const;
  if (status === "cancelled") return "error" as const;
  if (status === "contacted") return "warning" as const;
  return "default" as const;
};

const StaysPage = ({ initialTab = "settings" }: { initialTab?: StaysTab }) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<StaysTab>(initialTab);
  const [config, setConfig] = useState<Partial<StaysConfig>>(defaultConfig);
  const [categories, setCategories] = useState<StaysCategory[]>([]);
  const [properties, setProperties] = useState<StayProperty[]>([]);
  const [domains, setDomains] = useState<StoreDomain[]>([]);
  const [reservations, setReservations] = useState<StaysReservationRequest[]>([]);
  const [selectedPageId, setSelectedPageId] = useState<StorePageId>("home");
  const [categoryDraft, setCategoryDraft] = useState<Partial<StaysCategory>>(defaultCategory);
  const [propertyDraft, setPropertyDraft] = useState<Partial<StayProperty>>(defaultProperty);
  const [categoryEditingId, setCategoryEditingId] = useState<string | undefined>();
  const [propertyEditingId, setPropertyEditingId] = useState<string | undefined>();
  const [domainDraft, setDomainDraft] = useState("");
  const [loadError, setLoadError] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<SnackbarSeverity>("success");
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [propertyDialogOpen, setPropertyDialogOpen] = useState(false);
  const [domainDialogOpen, setDomainDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<StaysCategory | undefined>();
  const [propertyToDelete, setPropertyToDelete] = useState<StayProperty | undefined>();

  const pages = mergeExperiencePages(basePages, config.pages);
  const selectedPage = pages.find((page) => page.id === selectedPageId) || pages[0];

  const showSnackbar = (message: string, severity: SnackbarSeverity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const updateSelectedPage = (patch: Partial<StorePageConfig>) => {
    setConfig((current) => ({
      ...current,
      pages: mergeExperiencePages(basePages, current.pages).map((page) =>
        page.id === selectedPageId ? { ...page, ...patch } : page,
      ),
    }));
  };

  const resetCategoryDialog = () => {
    setCategoryEditingId(undefined);
    setCategoryDraft(defaultCategory);
    setCategoryDialogOpen(false);
  };

  const resetPropertyDialog = () => {
    setPropertyEditingId(undefined);
    setPropertyDraft(defaultProperty);
    setPropertyDialogOpen(false);
  };

  const openCategoryDialog = (category?: StaysCategory) => {
    setCategoryEditingId(category?.id);
    setCategoryDraft(category ? { ...category } : defaultCategory);
    setCategoryDialogOpen(true);
  };

  const openPropertyDialog = (property?: StayProperty) => {
    setPropertyEditingId(property?.id);
    setPropertyDraft(
      property
        ? {
            ...property,
            amenities: property.amenities || [],
          }
        : defaultProperty,
    );
    setPropertyDialogOpen(true);
  };

  const load = async () => {
    const [configResult, categoriesResult, propertiesResult, domainsResult, reservationsResult] = await Promise.all([
      staysApi.config.requestHard(null),
      staysApi.categories.requestHard(null),
      staysApi.properties.requestHard(null),
      staysApi.domains.requestHard(null),
      staysApi.reservations.requestHard(null),
    ]);

    if (configResult.isSuccess && configResult.data) {
      setConfig({
        ...defaultConfig,
        ...configResult.data,
        pages: mergeExperiencePages(basePages, configResult.data.pages),
      });
    }
    if (categoriesResult.isSuccess) setCategories(categoriesResult.data || []);
    if (propertiesResult.isSuccess) setProperties(propertiesResult.data || []);
    if (domainsResult.isSuccess) setDomains(domainsResult.data || []);
    if (reservationsResult.isSuccess) setReservations(reservationsResult.data || []);

    setLoadError(
      configResult.messageError ||
        categoriesResult.messageError ||
        propertiesResult.messageError ||
        domainsResult.messageError ||
        reservationsResult.messageError ||
        "",
    );
  };

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => setTab(initialTab), [initialTab]);

  const saveConfig = async (publish = false) => {
    const result = publish
      ? await staysApi.publish.requestHard({ config })
      : await staysApi.updateConfig.requestHard({ config });
    if (!result.isSuccess) {
      showSnackbar(result.messageError || "No pudimos guardar la configuración.", "error");
      return;
    }
    showSnackbar(publish ? "Mis hospedajes publicados." : "Configuración guardada.", "success");
    await load();
  };

  const saveCategory = async () => {
    if (!String(categoryDraft.name || "").trim()) {
      showSnackbar("El nombre de la categoría es requerido.", "error");
      return;
    }

    const result = categoryEditingId
      ? await staysApi.updateCategory(categoryEditingId).requestHard({ category: categoryDraft })
      : await staysApi.createCategory.requestHard({ category: categoryDraft });

    if (!result.isSuccess) {
      showSnackbar(result.messageError || "No pudimos guardar la categoría.", "error");
      return;
    }

    showSnackbar(categoryEditingId ? "Categoría actualizada." : "Categoría creada.", "success");
    resetCategoryDialog();
    await load();
  };

  const saveProperty = async () => {
    if (!String(propertyDraft.name || "").trim()) {
      showSnackbar("El nombre del hospedaje es requerido.", "error");
      return;
    }
    if (!String(propertyDraft.location || "").trim()) {
      showSnackbar("La ubicación del hospedaje es requerida.", "error");
      return;
    }

    const payload = {
      ...propertyDraft,
      amenities: String(propertyDraft.amenities || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean),
    };

    const result = propertyEditingId
      ? await staysApi.updateProperty(propertyEditingId).requestHard({ property: payload })
      : await staysApi.createProperty.requestHard({ property: payload });

    if (!result.isSuccess) {
      showSnackbar(result.messageError || "No pudimos guardar el hospedaje.", "error");
      return;
    }

    showSnackbar(propertyEditingId ? "Hospedaje actualizado." : "Hospedaje creado.", "success");
    resetPropertyDialog();
    await load();
  };

  const deleteCategory = async () => {
    if (!categoryToDelete) return;
    const result = await staysApi.deleteCategory(categoryToDelete.id).requestHard(null);
    if (!result.isSuccess) {
      showSnackbar(result.messageError || "No pudimos eliminar la categoría.", "error");
      return;
    }
    setCategoryToDelete(undefined);
    showSnackbar("Categoría eliminada.", "success");
    await load();
  };

  const deleteProperty = async () => {
    if (!propertyToDelete) return;
    const result = await staysApi.deleteProperty(propertyToDelete.id).requestHard(null);
    if (!result.isSuccess) {
      showSnackbar(result.messageError || "No pudimos eliminar el hospedaje.", "error");
      return;
    }
    setPropertyToDelete(undefined);
    showSnackbar("Hospedaje eliminado.", "success");
    await load();
  };

  const addDomain = async () => {
    if (!domainDraft.trim()) {
      showSnackbar("El dominio es requerido.", "error");
      return;
    }
    const result = await staysApi.addDomain.requestHard({ hostname: domainDraft.trim() });
    if (!result.isSuccess) {
      showSnackbar(result.messageError || "No pudimos agregar el dominio.", "error");
      return;
    }
    setDomainDraft("");
    setDomainDialogOpen(false);
    showSnackbar("Dominio agregado. Revisa las instrucciones DNS.", "success");
    await load();
  };

  const verifyDomain = async (hostname: string) => {
    const result = await staysApi.verifyDomain(hostname).requestHard(null);
    if (!result.isSuccess) {
      showSnackbar(result.messageError || "No pudimos verificar el dominio.", "error");
      return;
    }
    showSnackbar(
      result.data?.status === "active" ? "Dominio verificado." : "Aún no encontramos el DNS esperado.",
      result.data?.status === "active" ? "success" : "error",
    );
    await load();
  };

  const updateReservationStatus = async (reservationId: string, status: StaysReservationStatus) => {
    const result = await staysApi.updateReservationStatus(reservationId).requestHard({ status });
    if (!result.isSuccess) {
      showSnackbar(result.messageError || "No pudimos actualizar la reserva.", "error");
      return;
    }
    showSnackbar("Estado de la reserva actualizado.", "success");
    await load();
  };

  return (
    <Box sx={dashboardPageSx}>
      <Stack spacing={2} sx={dashboardPagePaddingSx}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} justifyContent="space-between">
          <Box>
            <Typography variant="h4" sx={dashboardSectionTitleSx}>
              Mis hospedajes
            </Typography>
            <Typography color="text.secondary">
              Administra propiedades, dominios y reservas con la misma estructura visual de Mi tienda.
            </Typography>
          </Box>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button
              variant="outlined"
              startIcon={<OpenInNew />}
              onClick={() => openExperiencePreview("stays", { config, categories, properties })}
            >
              Vista previa
            </Button>
            <Button variant="outlined" startIcon={<Save />} onClick={() => void saveConfig(false)}>
              Guardar
            </Button>
            <Button variant="contained" onClick={() => void saveConfig(true)}>
              Publicar
            </Button>
          </Stack>
        </Stack>

        {loadError ? <Alert severity="error">{loadError}</Alert> : null}

        <ToggleButtonGroup
          exclusive
          value={tab}
          sx={experienceToggleGroupSx}
          onChange={(_, value) => {
            if (!value) return;
            const nextTab = value as StaysTab;
            setTab(nextTab);
            navigate(tabPaths[nextTab]);
          }}
        >
          <ToggleButton value="settings">Configuración</ToggleButton>
          <ToggleButton value="design">Diseño</ToggleButton>
          <ToggleButton value="pages">Páginas</ToggleButton>
          <ToggleButton value="seo">SEO</ToggleButton>
          <ToggleButton value="properties">Propiedades</ToggleButton>
          <ToggleButton value="domains">Dominios</ToggleButton>
          <ToggleButton value="reservations">Reservas</ToggleButton>
        </ToggleButtonGroup>

        {tab === "settings" && (
          <Card sx={dashboardCardSx}>
            <CardContent>
              <Stack spacing={2.5}>
                <Box>
                  <Typography variant="h6">Configuración general</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Datos base para la marca, slug, textos comerciales y canales de contacto.
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Marca" value={config.brandName || ""} onChange={(event) => setConfig({ ...config, brandName: event.target.value })} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Slug" value={config.slug || ""} onChange={(event) => setConfig({ ...config, slug: event.target.value })} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Hero title" value={config.heroTitle || ""} onChange={(event) => setConfig({ ...config, heroTitle: event.target.value })} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Hero subtitle" value={config.heroSubtitle || ""} onChange={(event) => setConfig({ ...config, heroSubtitle: event.target.value })} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="WhatsApp" value={config.contactWhatsapp || ""} onChange={(event) => setConfig({ ...config, contactWhatsapp: event.target.value })} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Correo" value={config.contactEmail || ""} onChange={(event) => setConfig({ ...config, contactEmail: event.target.value })} />
                  </Grid>
                </Grid>
              </Stack>
            </CardContent>
          </Card>
        )}

        {tab === "design" && (
          <Stack spacing={2}>
            <Alert severity="info">
              Esta vertical debe respetar el patron de Mi tienda: tabs arriba, contenido abajo, modales para CRUD y snackbar para feedback.
            </Alert>
            <Card sx={dashboardCardSx}>
              <CardContent>
                <Stack spacing={2.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Palette fontSize="small" />
                    <Typography variant="h6">Diseño visual</Typography>
                  </Stack>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <TextField fullWidth label="Color principal" value={config.primaryColor || ""} onChange={(event) => setConfig({ ...config, primaryColor: event.target.value })} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField fullWidth label="Color secundario" value={config.secondaryColor || ""} onChange={(event) => setConfig({ ...config, secondaryColor: event.target.value })} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField fullWidth label="Fondo" value={config.backgroundColor || ""} onChange={(event) => setConfig({ ...config, backgroundColor: event.target.value })} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField fullWidth label="Color de texto" value={config.textColor || ""} onChange={(event) => setConfig({ ...config, textColor: event.target.value })} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField fullWidth label="Logo URL" value={config.logoUrl || ""} onChange={(event) => setConfig({ ...config, logoUrl: event.target.value })} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField fullWidth label="Hero image URL" value={config.heroImageUrl || ""} onChange={(event) => setConfig({ ...config, heroImageUrl: event.target.value })} />
                    </Grid>
                  </Grid>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        )}

        {tab === "pages" && selectedPage && (
          <Card sx={dashboardCardSx}>
            <CardContent>
              <Stack spacing={2.5}>
                <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between">
                  <Box>
                    <Typography variant="h6">Páginas internas</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Edita el contenido institucional que acompana la experiencia de hospedaje.
                    </Typography>
                  </Box>
                  <TextField
                    select
                    label="Página"
                    value={selectedPageId}
                    onChange={(event) => setSelectedPageId(event.target.value as StorePageId)}
                    sx={{ minWidth: { xs: "100%", md: 220 } }}
                  >
                    {pages.map((page) => (
                      <MenuItem key={page.id} value={page.id}>
                        {experiencePageLabels[page.id]}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Título visible" value={selectedPage.title} onChange={(event) => updateSelectedPage({ title: event.target.value })} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Slug" value={selectedPage.slug} onChange={(event) => updateSelectedPage({ slug: event.target.value })} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Título del contenido" value={selectedPage.contentTitle || ""} onChange={(event) => updateSelectedPage({ contentTitle: event.target.value })} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Label navbar" value={selectedPage.navLabel || ""} onChange={(event) => updateSelectedPage({ navLabel: event.target.value })} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth multiline minRows={5} label="Contenido" value={selectedPage.contentBody || ""} onChange={(event) => updateSelectedPage({ contentBody: event.target.value })} />
                  </Grid>
                </Grid>
              </Stack>
            </CardContent>
          </Card>
        )}

        {tab === "seo" && (
          <Card sx={dashboardCardSx}>
            <CardContent>
              <Stack spacing={2.5}>
                <Box>
                  <Typography variant="h6">SEO y metadatos</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Configura los metadatos básicos para la experiencia pública.
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="SEO title" value={config.seoTitle || ""} onChange={(event) => setConfig({ ...config, seoTitle: event.target.value })} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Tab title" value={config.tabTitle || ""} onChange={(event) => setConfig({ ...config, tabTitle: event.target.value })} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth multiline minRows={3} label="SEO description" value={config.seoDescription || ""} onChange={(event) => setConfig({ ...config, seoDescription: event.target.value })} />
                  </Grid>
                </Grid>
              </Stack>
            </CardContent>
          </Card>
        )}

        {tab === "properties" && (
          <Grid container spacing={2}>
            <Grid item xs={12} lg={4}>
              <Card sx={dashboardCardSx}>
                <CardContent>
                  <Stack spacing={2}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Category fontSize="small" />
                        <Typography variant="h6">Categorías</Typography>
                      </Stack>
                      <Button variant="contained" size="small" startIcon={<Add />} onClick={() => openCategoryDialog()}>
                        Crear categoría
                      </Button>
                    </Stack>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Nombre</TableCell>
                          <TableCell>Estado</TableCell>
                          <TableCell align="right">Acciones</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {categories.map((category) => (
                          <TableRow key={category.id}>
                            <TableCell>{category.name}</TableCell>
                            <TableCell>
                              <Chip size="small" label={category.isActive ? "Activa" : "Inactiva"} color={category.isActive ? "success" : "default"} />
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Editar categoría">
                                <IconButton size="small" onClick={() => openCategoryDialog(category)}>
                                  <Edit fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Eliminar categoría">
                                <IconButton size="small" color="error" onClick={() => setCategoryToDelete(category)}>
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} lg={8}>
              <Card sx={dashboardCardSx}>
                <CardContent>
                  <Stack spacing={2}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <HomeWork fontSize="small" />
                        <Typography variant="h6">Propiedades</Typography>
                      </Stack>
                      <Button variant="contained" size="small" startIcon={<Add />} onClick={() => openPropertyDialog()}>
                        Crear hospedaje
                      </Button>
                    </Stack>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Hospedaje</TableCell>
                          <TableCell>Ubicacion</TableCell>
                          <TableCell>Precio por noche</TableCell>
                          <TableCell>Capacidad</TableCell>
                          <TableCell>Estado</TableCell>
                          <TableCell align="right">Acciones</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {properties.map((property) => (
                          <TableRow key={property.id}>
                            <TableCell>{property.name}</TableCell>
                            <TableCell>{property.location}</TableCell>
                            <TableCell>${Number(property.pricePerNight || 0).toLocaleString("es-CO")}</TableCell>
                            <TableCell>{property.guestCapacity} huéspedes</TableCell>
                            <TableCell>
                              <Chip size="small" label={property.isActive ? "Activo" : "Inactivo"} color={property.isActive ? "success" : "default"} />
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Editar hospedaje">
                                <IconButton size="small" onClick={() => openPropertyDialog(property)}>
                                  <Edit fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Eliminar hospedaje">
                                <IconButton size="small" color="error" onClick={() => setPropertyToDelete(property)}>
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {tab === "domains" && (
          <Card sx={dashboardCardSx}>
            <CardContent>
              <Stack spacing={2.5}>
                <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1.5}>
                  <Box>
                    <Typography variant="h6">Dominios</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Publica la experiencia en subdominio LYPRO o en un dominio propio.
                    </Typography>
                  </Box>
                  <Button variant="contained" size="small" startIcon={<Domain />} onClick={() => setDomainDialogOpen(true)}>
                    Agregar dominio
                  </Button>
                </Stack>
                <Alert severity="info">
                  Agrega el dominio y luego usa Verificar para confirmar el apuntador DNS. La resolución pública sigue dependiendo del host.
                </Alert>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Hostname</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell align="right">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {domains.map((domain) => (
                      <TableRow key={domain.hostname}>
                        <TableCell>{domain.hostname}</TableCell>
                        <TableCell>
                          <Chip size="small" label={domain.status} color={domainStatusColor(domain.status)} />
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button size="small" onClick={() => openExperiencePreview("stays", { config, categories, properties }, domain.hostname)}>Probar página</Button>
                            <Button size="small" onClick={() => void verifyDomain(domain.hostname)}>Verificar</Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Stack>
            </CardContent>
          </Card>
        )}

        {tab === "reservations" && (
          <Card sx={dashboardCardSx}>
            <CardContent>
              <Stack spacing={2.5}>
                <Box>
                  <Typography variant="h6">Solicitudes de reserva</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Gestión manual de solicitudes que llegan desde la experiencia pública de hospedajes.
                  </Typography>
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Hospedaje</TableCell>
                      <TableCell>Cliente</TableCell>
                      <TableCell>Fechas</TableCell>
                      <TableCell>Huéspedes</TableCell>
                      <TableCell>Estado</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reservations.map((reservation) => (
                      <TableRow key={reservation.id}>
                        <TableCell>{reservation.propertyName}</TableCell>
                        <TableCell>
                          <Stack spacing={0.25}>
                            <Typography variant="body2">{reservation.customerName}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {reservation.customerPhone}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          {reservation.startDate} - {reservation.endDate}
                        </TableCell>
                        <TableCell>{reservation.guests}</TableCell>
                        <TableCell>
                          <TextField
                            select
                            size="small"
                            value={reservation.status}
                            sx={{ minWidth: 180 }}
                            onChange={(event) => void updateReservationStatus(reservation.id, event.target.value as StaysReservationStatus)}
                          >
                            {Object.entries(reservationStatusLabels).map(([value, label]) => (
                              <MenuItem key={value} value={value}>
                                {label}
                              </MenuItem>
                            ))}
                          </TextField>
                          <Chip
                            size="small"
                            label={reservationStatusLabels[reservation.status]}
                            color={reservationStatusColor(reservation.status)}
                            sx={{ ml: 1, display: { xs: "none", md: "inline-flex" } }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Stack>
            </CardContent>
          </Card>
        )}
      </Stack>

      <Dialog open={categoryDialogOpen} onClose={resetCategoryDialog} fullWidth maxWidth="sm">
        <DialogTitle>{categoryEditingId ? "Editar categoría" : "Crear categoría"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField fullWidth label="Nombre" value={categoryDraft.name || ""} onChange={(event) => setCategoryDraft({ ...categoryDraft, name: event.target.value })} />
            <TextField fullWidth label="Descripción" multiline minRows={3} value={categoryDraft.description || ""} onChange={(event) => setCategoryDraft({ ...categoryDraft, description: event.target.value })} />
            <TextField select fullWidth label="Estado" value={categoryDraft.isActive === false ? "inactive" : "active"} onChange={(event) => setCategoryDraft({ ...categoryDraft, isActive: event.target.value === "active" })}>
              <MenuItem value="active">Activa</MenuItem>
              <MenuItem value="inactive">Inactiva</MenuItem>
            </TextField>
            <TextField fullWidth type="number" label="Orden" value={categoryDraft.sortOrder ?? 0} onChange={(event) => setCategoryDraft({ ...categoryDraft, sortOrder: Number(event.target.value) })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={resetCategoryDialog}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={() => void saveCategory()}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={propertyDialogOpen} onClose={resetPropertyDialog} fullWidth maxWidth="md">
        <DialogTitle>{propertyEditingId ? "Editar hospedaje" : "Crear hospedaje"}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Nombre" value={propertyDraft.name || ""} onChange={(event) => setPropertyDraft({ ...propertyDraft, name: event.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Ubicacion" value={propertyDraft.location || ""} onChange={(event) => setPropertyDraft({ ...propertyDraft, location: event.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline minRows={3} label="Descripción" value={propertyDraft.description || ""} onChange={(event) => setPropertyDraft({ ...propertyDraft, description: event.target.value })} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField select fullWidth label="Categoría" value={propertyDraft.categoryId || ""} onChange={(event) => setPropertyDraft({ ...propertyDraft, categoryId: event.target.value })}>
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth type="number" label="Precio por noche" value={propertyDraft.pricePerNight ?? 0} onChange={(event) => setPropertyDraft({ ...propertyDraft, pricePerNight: Number(event.target.value) })} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth type="number" label="Huéspedes" value={propertyDraft.guestCapacity ?? 0} onChange={(event) => setPropertyDraft({ ...propertyDraft, guestCapacity: Number(event.target.value) })} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth type="number" label="Habitaciones" value={propertyDraft.bedroomCount ?? 0} onChange={(event) => setPropertyDraft({ ...propertyDraft, bedroomCount: Number(event.target.value) })} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth type="number" label="Banos" value={propertyDraft.bathroomCount ?? 0} onChange={(event) => setPropertyDraft({ ...propertyDraft, bathroomCount: Number(event.target.value) })} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth type="number" label="Orden" value={propertyDraft.sortOrder ?? 0} onChange={(event) => setPropertyDraft({ ...propertyDraft, sortOrder: Number(event.target.value) })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Imagen URL" value={propertyDraft.imageUrl || ""} onChange={(event) => setPropertyDraft({ ...propertyDraft, imageUrl: event.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Amenidades (separado por coma)" value={Array.isArray(propertyDraft.amenities) ? propertyDraft.amenities.join(", ") : String(propertyDraft.amenities || "")} onChange={(event) => setPropertyDraft({ ...propertyDraft, amenities: event.target.value as unknown as string[] })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Disponibilidad manual" value={propertyDraft.availabilityNotes || ""} onChange={(event) => setPropertyDraft({ ...propertyDraft, availabilityNotes: event.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField select fullWidth label="Estado" value={propertyDraft.isActive === false ? "inactive" : "active"} onChange={(event) => setPropertyDraft({ ...propertyDraft, isActive: event.target.value === "active" })}>
                <MenuItem value="active">Activo</MenuItem>
                <MenuItem value="inactive">Inactivo</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={resetPropertyDialog}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={() => void saveProperty()}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={domainDialogOpen} onClose={() => setDomainDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Agregar dominio</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField fullWidth label="Dominio personalizado" placeholder="stays.midominio.com" value={domainDraft} onChange={(event) => setDomainDraft(event.target.value)} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setDomainDialogOpen(false)}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={() => void addDomain()}>
            Agregar
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        open={Boolean(categoryToDelete)}
        title="Eliminar categoría"
        message={`Se eliminará la categoría ${categoryToDelete?.name || ""}.`}
        confirmColor="error"
        confirmText="Eliminar"
        onClose={() => setCategoryToDelete(undefined)}
        onConfirm={() => void deleteCategory()}
      />

      <ConfirmationDialog
        open={Boolean(propertyToDelete)}
        title="Eliminar hospedaje"
        message={`Se eliminará el hospedaje ${propertyToDelete?.name || ""}.`}
        confirmColor="error"
        confirmText="Eliminar"
        onClose={() => setPropertyToDelete(undefined)}
        onConfirm={() => void deleteProperty()}
      />

      <FeedbackSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={() => setSnackbarOpen(false)}
      />
    </Box>
  );
};

export default StaysPage;
